import { useEffect, useRef } from 'react'
import { Link, Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import { useShowBySlug } from '@/api/hooks'
import { useCurrentShow } from '@/context/current-show'
import NotFoundPage from '@/pages/NotFoundPage'
import { showAware } from '@/lib/paths'
import { useShowSlug } from '@/hooks/useShowSlug'
import { parseApiErrorStatus } from '@/lib/api-errors'

export function ShowSlugResolver() {
  const params = useParams<{ showSlug: string }>()
  const location = useLocation()
  const normalizedSlug = (params.showSlug ?? '').trim().toLowerCase()
  const lookupQuery = useShowBySlug(normalizedSlug ? { slug: normalizedSlug } : undefined)
  const appliedShow = useRef<typeof lookupQuery.data>(undefined)
  const resolvedIdentity = useRef<{ slug: string; id: string } | undefined>(undefined)
  const { currentShow, setCurrentShow, clearCurrentShow } = useCurrentShow()
  const currentShowId = currentShow?.id
  const hasCurrentShow = Boolean(currentShow)
  const errorStatus = parseApiErrorStatus(lookupQuery.error)
  const slugNotFound = lookupQuery.isError && errorStatus === 404
  const accessLost = lookupQuery.isError && [401, 403, 404].includes(errorStatus ?? 0)
  const transientFailure =
    lookupQuery.isError &&
    (errorStatus == null || errorStatus === 408 || errorStatus === 429 || errorStatus >= 500)
  const retainedResolution =
    transientFailure &&
    resolvedIdentity.current?.slug === normalizedSlug &&
    resolvedIdentity.current?.id === lookupQuery.data?.id

  useEffect(() => {
    if (!lookupQuery.isError && lookupQuery.data?.id) {
      resolvedIdentity.current = { slug: normalizedSlug, id: lookupQuery.data.id }
    }
    if (
      !lookupQuery.isError &&
      lookupQuery.data?.id &&
      (currentShowId !== lookupQuery.data.id || appliedShow.current !== lookupQuery.data)
    ) {
      appliedShow.current = lookupQuery.data
      setCurrentShow(lookupQuery.data)
      return
    }
    if (accessLost && hasCurrentShow) {
      resolvedIdentity.current = undefined
      clearCurrentShow()
    }
  }, [
    lookupQuery.data,
    lookupQuery.isError,
    currentShowId,
    hasCurrentShow,
    setCurrentShow,
    accessLost,
    normalizedSlug,
    clearCurrentShow,
  ])

  if (!normalizedSlug) {
    return <NotFoundPage />
  }

  if (
    (!lookupQuery.isError || retainedResolution) &&
    lookupQuery.data &&
    currentShowId === lookupQuery.data.id
  ) {
    const separator = location.pathname.indexOf('/', 1)
    const suffix = separator < 0 ? '' : location.pathname.slice(separator)
    return (
      <>
        {lookupQuery.data.slug && lookupQuery.data.slug !== normalizedSlug && (
          <Navigate
            replace
            to={`/${lookupQuery.data.slug}${suffix}${location.search}${location.hash}`}
          />
        )}
        <Outlet context={lookupQuery.data} />
      </>
    )
  }

  if (lookupQuery.isLoading || lookupQuery.isFetching) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6 text-muted-foreground">
        Selecting show…
      </div>
    )
  }

  if (slugNotFound) {
    return <NotFoundPage />
  }

  if (lookupQuery.isError) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 p-6 text-muted-foreground">
        <span>We ran into a problem selecting that show.</span>
        <span className="text-sm text-muted-foreground/80">
          Please refresh the page or try again later.
        </span>
        <Link to="/shows" className="underline">
          Go to Shows
        </Link>
      </div>
    )
  }

  if (!lookupQuery.data) {
    return <NotFoundPage />
  }

  return (
    <div className="flex min-h-[200px] items-center justify-center p-6 text-muted-foreground">
      Selecting show…
    </div>
  )
}

export function ShowSlugStatusRedirect() {
  const currentSlug = useShowSlug()
  const { currentShow } = useCurrentShow()
  const target = showAware(currentShow?.labelOnlyMode ? '/run' : '/status', { currentSlug })

  return <Navigate to={target} replace />
}
